import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Ion } from "@/components/Ion";
import { useBack } from "@/components/Orig";
import EstimateDisclaimerGate from "@/components/EstimateDisclaimerGate";
import { quoteBuilderApi } from "@/lib/quoteBuilder";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LinkBar } from "@/components/quote/LinkBar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ItemPicker, ItemRow, MaterialPicker, StairShortcut, sqIn } from "@/components/quote/ProjectItems";
import { HistoryPanel, MarketRange, SaveBar, VehicleShortcut } from "@/components/quote/ProjectExtras";
import { JobPhotos, PhotoScan, AiCheckNote } from "@/components/quote/PhotoScan";
import { Calibration } from "@/components/quote/Calibration";
import { emptyPatch, patchBody, patchRate } from "@/components/quote/TestPatch";
import { MissingList, flashTo } from "@/components/quote/MissingSteps";
import { Header, NumField, PrimaryBtn, ResultCard, Section, Switch, FieldLabel } from "@/components/quote/QuoteParts";

const num = (v) => parseFloat(v || "0");
let seq = 0;

const BuilderTabs = ({ builders, active, counts, onPick }) => (
  <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="project-builder-tabs">
    {builders.map((b) => (
      <button key={b.value} onClick={() => onPick(b.value)} data-testid={`project-tab-${b.value}`}
        className={`flex items-center justify-between rounded-[12px] border px-3 py-3 text-left text-[13px] transition-colors ${active === b.value ? "border-[#4CCC06] bg-[#4CCC0619] font-extrabold text-[#4CCC06]" : "border-[#333] bg-[#1A1A1A] font-semibold text-white hover:border-[#555]"}`}>
        {b.label}{counts[b.value] > 0 && <span className="ml-2 rounded-full bg-[#4CCC06] px-2 py-0.5 text-[10px] font-extrabold text-black" data-testid={`project-tab-count-${b.value}`}>{counts[b.value]}</span>}
      </button>
    ))}
  </div>
);

const Economics = ({ e, set }) => (
  <Section title="YOUR ECONOMICS (INTERNAL)">
    <div className="mb-3 flex items-center justify-between"><FieldLabel>Remove &amp; reinstall door/drawer hardware?</FieldLabel><Switch on={e.hardware} onClick={() => set({ hardware: !e.hardware })} testId="project-hardware-switch" /></div>
    {e.hardware && <NumField label="Hardware fee per door / drawer ($)" value={e.hwFee} onChange={(v) => set({ hwFee: v })} fullWidth testId="project-hardware-fee" />}
    <NumField label="Your hourly rate ($/hr)" value={e.hourly} onChange={(v) => set({ hourly: v })} fullWidth testId="project-hourly" />
    <div className="mt-1.5 flex gap-2.5"><NumField label="Setup (min)" value={e.setup} onChange={(v) => set({ setup: v })} testId="project-setup" /><NumField label="Travel fee ($)" value={e.travel} onChange={(v) => set({ travel: v })} testId="project-travel" /></div>
    <NumField label="Consumables ($)" value={e.consumables} onChange={(v) => set({ consumables: v })} fullWidth testId="project-consumables" />
  </Section>
);

const CombineCard = ({ combine, set }) => (
  <Section title="HOW TO PRICE IT" testId="project-combine-card">
    <div className="flex items-center justify-between gap-3">
      <div><p className="text-[13px] font-bold">Combine into one job quote</p><p className="mt-0.5 text-[11px] text-[#A0A0A0]">{combine ? "One price for everything, setup and travel counted once." : "A separate price for each builder, each with its own setup and travel."}</p></div>
      <Switch on={combine} onClick={() => set(!combine)} testId="project-combine-switch" />
    </div>
  </Section>
);

export default function ProjectQuote() {
  const safeBack = useBack();
  const [presets, setPresets] = useState(null);
  const [active, setActive] = useState("kitchen");
  const [items, setItems] = useState([]);
  const [mats, setMats] = useState({});
  const [combine, setCombine] = useState(true);
  const [econ, setEcon] = useState({ hardware: false, hwFee: "35", hourly: "", setup: "30", travel: "0", consumables: "0" });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const nav = useNavigate();
  const [billingOn, setBillingOn] = useState(false);
  useEffect(() => { api("/billing/status").then((s) => setBillingOn(!!s.enabled)).catch(() => {}); }, []);
  const [loaded, setLoaded] = useState(null);
  const [histKey, setHistKey] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [refs, setRefs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [link, setLink] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    quoteBuilderApi.projectPresets().then((p) => {
      setPresets(p);
      setMats(Object.fromEntries(p.builders.map((b) => [b.value, b.value === "automotive" ? { material: "automotive", species: "factory_paint", patch: emptyPatch() }
        : { material: "wood", species: "oak", patch: emptyPatch() }])));
      setEcon((e) => ({ ...e, hwFee: String(p.default_hardware_fee_per_door), setup: String(p.default_setup_minutes) }));
    }).catch((e) => window.alert(`Couldn't load the quote builder\n\n${e?.message || ""}`));
  }, []);

  const byType = useMemo(() => Object.fromEntries((presets?.items || []).map((p) => [p.value, p])), [presets]);
  const counts = useMemo(() => items.reduce((a, it) => ({ ...a, [byType[it.type].builder]: (a[byType[it.type].builder] || 0) + 1 }), {}), [items, byType]);
  const used = presets ? presets.builders.filter((b) => counts[b.value]) : [];
  const aiCount = items.filter((x) => x.ai === true).length;
  const matPicker = (b) => b && mats[b.value] && <MaterialPicker key={b.value} label={b.label} builder={b.value} presets={presets} m={mats[b.value]} onChange={(ch) => setMats((m) => ({ ...m, [b.value]: { ...m[b.value], ...ch } }))} testId={`project-mat-${b.value}`} />;
  const totalSq = items.reduce((s, it) => s + sqIn(byType[it.type], it), 0);

  const row = (p, ch) => ({ id: `i${++seq}`, type: p.value, qty: "1", w: p.w ? String(p.w) : "", h: p.h ? String(p.h) : "", d: p.d ? String(p.d) : "", extra: String(p.extra || 0), sides: p.sides, mode: "dims", sqft: "", ...ch });
  const add = (p) => setItems((xs) => [...xs, row(p)]);
  const fillStairs = (n, k) => setItems((xs) => {
    let out = [...xs];
    [["tread", n], ["riser", n], ["spindle", n * k]].forEach(([t, q]) => {
      if (!q) return;
      const i = out.findIndex((x) => x.type === t);
      if (i >= 0) out[i] = { ...out[i], qty: String(q) }; else out = [...out, row(byType[t], { qty: String(q) })];
    });
    return out;
  });
  const fillVehicle = (k) => setItems((xs) => {
    let out = [...xs];
    presets.vehicles[k].forEach((v) => {
      const ch = { qty: String(v.qty), w: String(v.w), h: String(v.h) };
      const i = out.findIndex((x) => x.type === v.type);
      if (i >= 0) out[i] = { ...out[i], ...ch }; else out = [...out, row(byType[v.type], ch)];
    });
    return out;
  });
  const saveHistory = async (name, asNew) => {
    const body = { name, inputs: { items, mats, combine, econ, notes, photos, refs, link, videos }, summary: { builders: used.map((b) => b.label).join(" + "), price: result ? result.quotes.map((q) => q.result.customer.quoted_price_display).join(" + ") : "" } };
    const d = await api(asNew ? "/quote-builder/history" : `/quote-builder/history/${loaded.id}`, { method: asNew ? "POST" : "PUT", body });
    setLoaded(d); setHistKey((k) => k + 1);
    toast.success(asNew ? `Saved "${name}" to Quote History` : "Changes saved");
    return d;
  };
  const openSaved = (d) => {
    const inp = d.inputs || {};
    seq += 1000;
    setItems((inp.items || []).map((x, i) => ({ ...x, id: `h${seq}-${i}` })));
    setMats((m) => ({ ...m, ...Object.fromEntries(Object.entries(inp.mats || {}).map(([k, v]) => [k, { material: v.material, species: v.species, patch: v.patch || emptyPatch() }])) }));
    setCombine(inp.combine ?? true);
    setEcon((e) => ({ ...e, ...(inp.econ || {}) }));
    setNotes(inp.notes || "");
    setPhotos(inp.photos || []);
    setRefs(inp.refs || []);
    setVideos(inp.videos || []);
    setLink(inp.link || null);
    setResult(null); setAccepted(false); setLoaded(d);
    const first = (inp.items || [])[0];
    if (first && byType[first.type]) setActive(byType[first.type].builder);
    toast.success(`Opened "${d.name}"`);
  };
  const addScanned = (rows, ids, replace = []) => {
    const s = (v) => (v ? String(v) : "");
    const made = rows.map((r) => row(byType[r.type], { qty: String(r.qty), w: s(r.width_in), h: s(r.height_in), d: s(r.depth_in), sides: r.sides, est: !!r.estimated, ai: true,
      ...(r.sq_ft ? { mode: "sqft", sqft: String(r.sq_ft) } : {}) }));
    setItems((xs) => [...xs.filter((x) => !replace.includes(x.id)), ...made]);
    setPhotos((p) => [...new Set([...p, ...ids])]);
    return made.map((m) => m.id);
  };
  useEffect(() => {
    if (!presets) return;
    const g = (k) => params.get(k) || "";
    if (g("open")) api(`/quote-builder/history/${g("open")}`).then(openSaved).catch((e) => toast.error(e.message));
    else if (g("customer_id")) setLink({ customer_id: g("customer_id"), customer_name: g("customer_name"), job_id: g("job_id") || null, job_number: g("job_number") || null, job_title: g("job_title") });
  }, [presets]); // eslint-disable-line react-hooks/exhaustive-deps
  const toInvoicing = async (q) => {
    if (link) {
      try {
        const saved = loaded || await saveHistory(`${link.customer_name}${link.job_number ? ` · Job #${link.job_number}` : ""} · ${q.label}`, true);
        if (loaded) await saveHistory(loaded.name, false);
        const r = await api("/quote-builder/send-to-invoicing", { method: "POST", body: { history_id: saved.id, customer_id: link.customer_id, job_id: link.job_id, title: q.label, notes: q.result.customer.description, lines: q.lines.map((l) => ({ ...l, category: "service" })) } });
        toast.success(r.mode === "updated" ? "Draft quote updated" : "Draft quote created" + (r.job_id ? " and job lines filled" : ""));
        return nav(`/billing/doc/${r.doc_id}`);
      } catch (e) { return toast.error(e.message); }
    }
    sessionStorage.setItem("alr_quote_prefill", JSON.stringify({ title: q.label, notes: q.result.customer.description, lines: q.lines }));
    nav("/billing/new/quote");
  };
  const patch = (id, ch) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...("style" in ch ? {} : { est: false }), ...ch } : x)));
  const sameStyle = (b, it) => items.filter((x) => byType[x.type].builder === b && byType[x.type].style_group === byType[it.type].style_group);
  const applyStyle = (b, it, style) => {
    const ids = new Set(sameStyle(b, it).map((x) => x.id));
    setItems((xs) => xs.map((x) => (ids.has(x.id) ? { ...x, style } : x)));
    toast.success(`Style applied to ${ids.size} parts`);
  };

  const missing = (() => {
    if (!presets) return [];
    const out = [];
    if (!items.length) out.push({ msg: "Add at least one part: tap a picture card or quote from a photo", target: "project-builder-tabs" });
    const seen = {};
    items.forEach((it) => {
      const m = byType[it.type].measure, sq = m === "floor" && it.mode === "sqft";
      seen[it.type] = (seen[it.type] || 0) + 1;
      const name = `${byType[it.type].label}${items.filter((x) => x.type === it.type).length > 1 ? ` #${seen[it.type]}` : ""}`;
      const sizesOk = sq ? num(it.sqft) > 0 : num(it.w) > 0 && num(it.h) > 0 && (!["box", "firebox", "arch"].includes(m) || num(it.d) > 0);
      if (!(parseInt(it.qty, 10) >= 1) || !sizesOk) out.push({ msg: `${name}: fill in the quantity and every size`, target: `project-row-${it.id}` });
      if (it.patch && !patchRate(it.patch)) out.push({ msg: `${name}: finish its own test patch (size and time) or tap USE MAIN PATCH`, target: `project-patch-${it.id}` });
    });
    used.forEach((b) => { if (!patchRate(mats[b.value].patch)) out.push({ msg: `${b.label}: enter the main test patch size and how long it took`, target: `project-mat-${b.value}-patch` }); });
    if (num(econ.hourly) <= 0) out.push({ msg: "Enter your hourly rate", target: "project-hourly" });
    return out;
  })();

  const submit = async () => {
    if (missing.length) return flashTo(missing[0].target);
    setBusy(true); setAccepted(false);
    try {
      setResult(await quoteBuilderApi.calculateProject({
        items: items.map((it) => {
          const m = byType[it.type].measure, sq = m === "floor" && it.mode === "sqft";
          return { type: it.type, qty: parseInt(it.qty, 10), width_in: sq ? 1 : num(it.w), height_in: sq ? 1 : num(it.h), sides: it.sides,
            ...(m === "box" ? { depth_in: num(it.d), extra_pct: num(it.extra) } : {}), ...(["firebox", "arch"].includes(m) ? { depth_in: num(it.d) } : {}), ...(sq ? { sq_ft: num(it.sqft) } : {}),
            ...(it.patch ? { patch: patchBody(it.patch) } : {}), ...(it.style ? { style: it.style } : {}) };
        }),
        materials: Object.fromEntries(used.map((b) => [b.value, { material: mats[b.value].material, species: mats[b.value].species, patch: patchBody(mats[b.value].patch) }])),
        combine, hardware_removal: econ.hardware, hardware_fee_per_door: num(econ.hwFee), desired_hourly_rate: num(econ.hourly),
        setup_minutes: num(econ.setup), travel_fee: num(econ.travel), consumables_fee: num(econ.consumables), project_notes: notes.trim() || null,
      }));
    } catch (e) { window.alert(`Couldn't build quote\n\n${e?.message || "Try again."}`); } finally { setBusy(false); }
  };
  const restart = () => { setResult(null); setAccepted(false); };

  if (!presets) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#4CCC06]" /></div>;
  if (result) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="door-quote-page">
      <Header title="Project Quote" subtitle={accepted ? "Result" : "Acknowledge before viewing"} onBack={restart} />
      {accepted ? (
        <div className="mx-auto max-w-3xl p-4 pb-36" data-testid="project-results">
          {aiCount > 0 && <AiCheckNote n={aiCount} testId="project-results-ai-note" />}
          {result.quotes.map((q, i) => (
            <div key={q.builder}>
              <ResultCard label={q.label} r={q.result} idx={i} />
              {q.lines.some((l) => l.description.includes("\nStyle: ")) && (
                <div className="-mt-3 mb-8 rounded-[14px] border border-[#333] bg-[#121212] p-4" data-testid={`project-result-styles-${i}`}>
                  <p className="mb-1 text-[10px] font-extrabold tracking-[2px] text-[#4CCC06]">STYLES ON THE CUSTOMER QUOTE</p>
                  {q.lines.filter((l) => l.description.includes("\nStyle: ")).map((l, j) => <p key={j} className="mt-1 text-xs"><b>{l.description.split("\n")[0].replace("Laser cleaning: ", "")}</b> <span className="text-[#A0A0A0]">· {l.description.split("\nStyle: ")[1]}</span></p>)}
                </div>
              )}
              <MarketRange lines={q.lines} labels={Object.fromEntries(presets.items.map((p) => [p.value, p.label]))} idx={i} />
              {billingOn && <button onClick={() => toInvoicing(q)} data-testid={`project-to-invoicing-${i}`} className="-mt-3 mb-8 flex w-full items-center justify-center rounded-lg border border-[#4CCC06] bg-[#4CCC0614] py-3 text-xs font-extrabold tracking-[1px] text-[#4CCC06] transition-colors hover:bg-[#4CCC0626]"><Ion name="document-text-outline" size={14} color="#4CCC06" />&nbsp;&nbsp;SEND TO INVOICING AS A DRAFT QUOTE</button>}
            </div>
          ))}
          <SaveBar loaded={loaded} onSave={saveHistory} testId="history-save-result" />
          <PrimaryBtn onClick={restart} data-testid="project-edit">&nbsp;&nbsp;BACK TO EDIT</PrimaryBtn>
        </div>
      ) : <EstimateDisclaimerGate onAccept={() => setAccepted(true)} onBack={restart} />}
    </div>
  );
  const activeLabel = presets.builders.find((b) => b.value === active).label;
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="door-quote-page">
      <Header title="Project Quote" subtitle="Kitchens · doors · stairs · furniture · fireplaces · floors · automotive" onBack={safeBack} />
      <div className="mx-auto max-w-3xl p-4 pb-36">
        {billingOn && <LinkBar link={link} setLink={setLink} />}
        <HistoryPanel onOpen={openSaved} reloadKey={histKey} />
        {loaded && <p className="mb-2 px-1 text-[11px] text-[#A0A0A0]" data-testid="history-loaded">Editing saved quote <b className="text-white">{loaded.name}</b></p>}
        <BuilderTabs builders={presets.builders} active={active} counts={counts} onPick={setActive} />
        {matPicker(presets.builders.find((b) => b.value === active))}
        <JobPhotos ids={photos} videos={videos} />
        <Calibration refs={refs} setRefs={setRefs} />
        <PhotoScan builder={active} label={activeLabel} byType={byType} onAdd={addScanned} refs={refs} onVideo={(v) => setVideos((x) => [...x, v])} />
        {active === "automotive" && <VehicleShortcut onFill={fillVehicle} />}
        {active === "staircase" && <StairShortcut onFill={fillStairs} hasSkirt={items.some((x) => x.type === "skirt_board")} onAddSkirt={() => add(byType.skirt_board)} />}
        <Section title={`${activeLabel.toUpperCase()} · TAP A PICTURE TO ADD IT`}><ItemPicker items={presets.items.filter((p) => p.builder === active)} onAdd={add} /></Section>
        {presets.builders.filter((b) => counts[b.value]).map((b) => (
          <Section key={b.value} title={`${b.label.toUpperCase()} · ${counts[b.value]} ITEM${counts[b.value] > 1 ? "S" : ""}`} testId={`project-list-${b.value}`}>
            {items.filter((it) => byType[it.type].builder === b.value).map((it) => <ItemRow key={it.id} p={byType[it.type]} it={it} mat={mats[b.value]} builder={b.value} styles={presets.styles} sameCount={sameStyle(b.value, it).length} onApplyStyle={(style) => applyStyle(b.value, it, style)} onChange={(ch) => patch(it.id, ch)} onRemove={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} />)}
            <button onClick={() => { setActive(b.value); document.querySelector('[data-testid="project-builder-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }} data-testid={`project-add-more-${b.value}`} className="mt-1 flex w-full items-center justify-center rounded-[10px] border border-dashed border-[#4CCC06] py-2.5 text-[11px] font-extrabold tracking-[1px] text-[#4CCC06] transition-colors hover:bg-[#4CCC060F]"><Ion name="add" size={14} color="#4CCC06" />&nbsp;ADD ANOTHER</button>
          </Section>
        ))}
        {totalSq > 0 && <p className="mb-3 px-1 text-[11px] text-[#A0A0A0]" data-testid="project-total-area">Total cleanable area: <b className="text-white">{Math.round(totalSq).toLocaleString()} sq in</b> · {(totalSq / 144).toFixed(1)} sq ft</p>}
        {used.filter((b) => b.value !== active).map(matPicker)}
        {used.length > 1 && <CombineCard combine={combine} set={setCombine} />}
        <Economics e={econ} set={(ch) => setEcon((x) => ({ ...x, ...ch }))} />
        <Section title="NOTES (optional)">
          <textarea data-testid="project-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything special? e.g. lead paint, antique, customer wants natural finish…" className="min-h-[80px] w-full rounded-[10px] border border-[#333] bg-[#121212] p-3 text-[13px] text-white placeholder:text-[#A0A0A0] focus:outline-none" />
        </Section>
        {items.length > 0 && <SaveBar loaded={loaded} onSave={saveHistory} />}
        {aiCount > 0 && <AiCheckNote n={aiCount} testId="project-ai-note" />}
        <MissingList items={missing} testId="project-missing" />
        <PrimaryBtn onClick={submit} disabled={busy} style={missing.length ? { opacity: 0.6 } : undefined} data-testid="project-calculate">{busy ? <Loader2 size={18} className="animate-spin" /> : <><Ion name="calculator" size={14} color="#000" />&nbsp;&nbsp;CALCULATE QUOTE</>}</PrimaryBtn>
      </div>
    </div>
  );
}
